import { useState } from "react";
import { Search, ChevronDown, RotateCcw, Plus, Pencil, Trash2, X, Cpu } from "lucide-react";

const STATUS_STYLES = {
    Available: "bg-emerald-50 text-emerald-600",
    Running: "bg-blue-50 text-blue-600",
    Maintenance: "bg-pink-50 text-pink-600",
    Booked: "bg-amber-50 text-amber-600",
};

const CATEGORIES = [
    "All categories",
    "3D Printing",
    "CNC",
    "Laser Cutting",
    "PCB",
    "Moulding",
    "Robotics",
];

const STATUS_OPTIONS = ["Available", "Running", "Maintenance", "Booked"];

const INITIAL_MACHINES = [
    { id: "M-3DP-01", name: "Industrial FDM 3D Printer", category: "3D Printing", centre: "APIC Visakhapatnam", make: "Stratasys F770", rate: 850, status: "Available" },
    { id: "M-CNC-05", name: "5-Axis CNC Machining Centre", category: "CNC", centre: "APIC Vijayawada", make: "DMG Mori DMU 50", rate: 2400, status: "Running" },
    { id: "M-LSR-02", name: "Fiber Laser Cutter 3kW", category: "Laser Cutting", centre: "APIC Tirupati", make: "Trumpf TruLaser 3030", rate: 1600, status: "Available" },
    { id: "M-PCB-03", name: "PCB Prototyping Machine", category: "PCB", centre: "APIC Visakhapatnam", make: "LPKF ProtoMat S104", rate: 1100, status: "Maintenance" },
    { id: "M-IMM-01", name: "Injection Moulding 120T", category: "Moulding", centre: "APIC Kurnool", make: "Arburg Allrounder 470", rate: 1900, status: "Booked" },
    { id: "M-ROB-04", name: "6-Axis Industrial Robotic Arm", category: "Robotics", centre: "APIC Vijayawada", make: "KUKA KR 10 R1420", rate: 1400, status: "Available" },
];

const EMPTY_FORM = { id: "", name: "", category: CATEGORIES[1], centre: "", make: "", rate: "", status: STATUS_OPTIONS[0] };

export default function Machines() {
    const [machines, setMachines] = useState(INITIAL_MACHINES);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All categories");
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const filteredMachines = machines.filter((m) => {
        const matchesSearch =
            search.trim() === "" ||
            m.name.toLowerCase().includes(search.toLowerCase()) ||
            m.id.toLowerCase().includes(search.toLowerCase()) ||
            m.make.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = category === "All categories" || m.category === category;
        return matchesSearch && matchesCategory;
    });

    const handleReset = () => {
        setSearch("");
        setCategory("All categories");
    };

    const openAddModal = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setShowModal(true);
    };

    const openEditModal = (machine) => {
        setEditingId(machine.id);
        setForm({ ...machine, rate: String(machine.rate) });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setForm(EMPTY_FORM);
        setEditingId(null);
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleDelete = (id) => {
        setMachines((prev) => prev.filter((m) => m.id !== id));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.id.trim() || !form.centre.trim() || !form.make.trim() || !form.rate) return;

        const machineData = {
            id: form.id.trim(),
            name: form.name.trim(),
            category: form.category,
            centre: form.centre.trim(),
            make: form.make.trim(),
            rate: Number(form.rate),
            status: form.status,
        };

        if (editingId) {
            setMachines((prev) => prev.map((m) => (m.id === editingId ? machineData : m)));
        } else {
            setMachines((prev) => [...prev, machineData]);
        }
        closeModal();
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Machines</h1>
                <p className="mt-1 text-gray-500">
                    Manage your centre's machine catalog — visible to innovators for booking
                </p>
            </div>

            {/* Toolbar */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, ID, make..."
                        className="w-full rounded-full border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-700 shadow-sm outline-none placeholder:text-gray-400 focus:border-blue-400"
                    />
                </div>

                <div className="relative">
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="appearance-none rounded-full border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm text-gray-700 shadow-sm outline-none focus:border-blue-400"
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>

                <button
                    onClick={handleReset}
                    className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                >
                    <RotateCcw size={16} />
                    Reset
                </button>

                <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                >
                    <Plus size={16} />
                    Add Machine
                </button>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <table className="w-full text-left">
                    <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/60 text-xs font-semibold uppercase tracking-wider text-gray-400">
                            <th className="px-6 py-4">Machine</th>
                            <th className="px-6 py-4">Category</th>
                            <th className="px-6 py-4">Centre</th>
                            <th className="px-6 py-4">Make</th>
                            <th className="px-6 py-4 text-right">Rate</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMachines.map((machine) => (
                            <tr key={machine.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 text-white">
                                            <Cpu size={18} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{machine.name}</p>
                                            <p className="text-xs text-gray-400">{machine.id}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">{machine.category}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{machine.centre}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{machine.make}</td>
                                <td className="px-6 py-4 text-right text-sm font-semibold text-blue-600">
                                    ₹{machine.rate.toLocaleString("en-IN")}/hr
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[machine.status] || "bg-gray-50 text-gray-600"}`}>
                                        {machine.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center justify-end gap-3">
                                        <button onClick={() => openEditModal(machine)} className="text-gray-400 transition hover:text-gray-700" aria-label={`Edit ${machine.name}`}>
                                            <Pencil size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(machine.id)} className="text-pink-400 transition hover:text-pink-600" aria-label={`Delete ${machine.name}`}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}

                        {filteredMachines.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-400">
                                    No machines match your search.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add / Edit Machine Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeModal}>
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
                            <h2 className="text-lg font-bold text-gray-900">{editingId ? "Edit Machine" : "Add Machine"}</h2>
                            <button onClick={closeModal} className="text-gray-400 transition hover:text-gray-700" aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto px-6 py-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Machine Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={(e) => handleFormChange("name", e.target.value)}
                                        placeholder="e.g. Industrial FDM 3D Printer"
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Machine ID</label>
                                    <input
                                        type="text"
                                        required
                                        disabled={!!editingId}
                                        value={form.id}
                                        onChange={(e) => handleFormChange("id", e.target.value)}
                                        placeholder="e.g. M-3DP-01"
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Category</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => handleFormChange("category", e.target.value)}
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                                    >
                                        {CATEGORIES.filter((c) => c !== "All categories").map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Centre</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.centre}
                                        onChange={(e) => handleFormChange("centre", e.target.value)}
                                        placeholder="e.g. APIC Visakhapatnam"
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Make / Model</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.make}
                                        onChange={(e) => handleFormChange("make", e.target.value)}
                                        placeholder="e.g. Stratasys F770"
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Rate (₹/hr)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        required
                                        value={form.rate}
                                        onChange={(e) => handleFormChange("rate", e.target.value)}
                                        placeholder="e.g. 850"
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Status</label>
                                    <select
                                        value={form.status}
                                        onChange={(e) => handleFormChange("status", e.target.value)}
                                        className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-5">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                                >
                                    {editingId ? "Save Changes" : "Add Machine"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}